// © 2023 Amazon Web Services, Inc. or its affiliates. All Rights Reserved.
// This AWS Content is provided subject to the terms of the AWS Customer Agreement available at
// http://aws.amazon.com/agreement or other written agreement between Customer and either
// Amazon Web Services, Inc. or Amazon Web Services EMEA SARL or both.
import { generateClient } from "aws-amplify/api"
import {
  getAccounts,
  getPermissions,
  getApprovers,
  listRequests,
  getRequests,
  getSessions,
  listApprovers,
  getLogs,
  getOUs,
  getOU,
  requestByEmailAndStatus,
  getIdCGroups,
  getUsers,
  listEligibilities,
  getEligibility,
  listGroups,
  getSettings,
  getMgmtPermissions,
  getUserPolicy
} from "../../graphql/queries";
import {
  createRequests,
  createSessions,
  deleteSessions,
  updateRequests,
  createApprovers,
  deleteApprovers,
  updateApprovers,
  createEligibility,
  deleteEligibility,
  updateEligibility,
  createSettings,
  updateSettings
} from "../../graphql/mutations";
import { list } from "aws-amplify/storage";

export async function fetchAccounts() {
  const client = generateClient();
  try {
    const result = await client.graphql({ query: getAccounts });
    const data =
      result?.data?.getAccounts ||
      result?.value?.data?.getAccounts ||
      [];
    return data;
  } catch (err) {
    console.log("error fetching accounts");
    return [];
  }
}

export async function fetchPermissions() {
  const client = generateClient();
  try {
    const result = await client.graphql({ query: getPermissions });
    const data =
      result?.data?.getPermissions ||
      result?.value?.data?.getPermissions ||
      [];
    return data;
  } catch (err) {
    console.log("error fetching permissions", err);
    return [];
  }
}

export async function getMgmtAccountPs() {
  const client = generateClient();
  try {
    const result = await client.graphql({ query: getMgmtPermissions });
    const data =
      result?.data?.getMgmtPermissions ||
      result?.value?.data?.getMgmtPermissions ||
      [];
    return data;
  } catch (err) {
    console.log("error fetching permissions");
    return [];
  }
}

export async function getUserRequests(email) {
  const client = generateClient();
  let nextToken = null;
  let data = [];
  try {
    do {
      const result = await client.graphql({
        query: requestByEmailAndStatus,
        variables: { email, nextToken }
      });
      const items =
        result?.data?.requestByEmailAndStatus?.items ||
        result?.value?.data?.requestByEmailAndStatus?.items ||
        [];
      data = data.concat(items);
      nextToken =
        result?.data?.requestByEmailAndStatus?.nextToken ||
        result?.value?.data?.requestByEmailAndStatus?.nextToken;
    } while (nextToken);
    return data;
  } catch (err) {
    console.log("error fetching requests", err);
    return [];
  }
}

export async function fetchOUs() {
  const client = generateClient();
  try {
    const result = await client.graphql({ query: getOUs });
    const data =
      result?.data?.getOUs ||
      result?.value?.data?.getOUs ||
      [];
    return data;
  } catch (err) {
    console.log("error fetching OUs", err);
    return [];
  }
}

export async function fetchOU(id) {
  const client = generateClient();
  try {
    const result = await client.graphql({
      query: getOU,
      variables: { id }
    });
    const data =
      result?.data?.getOU ||
      result?.value?.data?.getOU ||
      {};
    return data;
  } catch (err) {
    console.log("error fetching OU");
    return {};
  }
}

export async function getGroupMemberships(id) {
  const client = generateClient();
  try {
    const result = await client.graphql({
      query: listGroups,
      variables: { groupIds: id }
    });
    const data =
      result?.data?.listGroups ||
      result?.value?.data?.listGroups ||
      [];
    return data;
  } catch (err) {
    console.log("error fetching members");
    return [];
  }
}

export async function fetchIdCGroups() {
  const client = generateClient();
  try {
    const result = await client.graphql({ query: getIdCGroups });
    const data =
      result?.data?.getIdCGroups ||
      result?.value?.data?.getIdCGroups ||
      [];
    return data;
  } catch (err) {
    console.log("error fetching IdC Groups");
    return [];
  }
}

export async function fetchUsers() {
  const client = generateClient();
  try {
    const result = await client.graphql({ query: getUsers });
    const data =
      result?.data?.getUsers ||
      result?.value?.data?.getUsers ||
      [];
    return data;
  } catch (err) {
    console.log("error fetching IdC Groups");
    return [];
  }
}

export async function getSessionList() {
  const client = generateClient();
  let nextToken = null;
  let data = [];
  try {
    do {
      const result = await client.graphql({
        query: listRequests,
        variables: { nextToken }
      });
      const items =
        result?.data?.listRequests?.items ||
        result?.value?.data?.listRequests?.items ||
        [];
      data = data.concat(items);
      nextToken =
        result?.data?.listRequests?.nextToken ||
        result?.value?.data?.listRequests?.nextToken;
    } while (nextToken);
    return data;
  } catch (err) {
    console.log("error fetching sessions");
    return [];
  }
}

export async function getRequest(id) {
  const client = generateClient();
  try {
    const result = await client.graphql({
      query: getRequests,
      variables: { id }
    });
    const data =
      result?.data?.getRequests ||
      result?.value?.data?.getRequests ||
      {};
    return data;
  } catch (err) {
    console.log("error fetching request");
    return {};
  }
}

export async function getAllApprovers() {
  const client = generateClient();
  let nextToken = null;
  let data = [];
  try {
    do {
      const result = await client.graphql({
        query: listApprovers,
        variables: { nextToken }
      });
      const items =
        result?.data?.listApprovers?.items ||
        result?.value?.data?.listApprovers?.items ||
        [];
      data = data.concat(items);
      nextToken =
        result?.data?.listApprovers?.nextToken ||
        result?.value?.data?.listApprovers?.nextToken;
    } while (nextToken);
    return data;
  } catch (err) {
    console.log("error fetching approvers", err);
    return [];
  }
}

export async function sessions(filter) {
  const client = generateClient();
  let nextToken = null;
  let data = [];
  try {
    do {
      const result = await client.graphql({
        query: listRequests,
        variables: { filter, nextToken }
      });
      const items =
        result?.data?.listRequests?.items ||
        result?.value?.data?.listRequests?.items ||
        [];
      data = data.concat(items);
      nextToken =
        result?.data?.listRequests?.nextToken ||
        result?.value?.data?.listRequests?.nextToken;
    } while (nextToken);
    return data;
  } catch (err) {
    console.log("error fetching sessions");
    return [];
  }
}

export async function fetchLogs(args) {
  const client = generateClient();
  try {
    console.log("fetchLogs called with args:", args);
    const result = await client.graphql({
      query: getLogs,
      variables: args
    });
    console.log("fetchLogs raw result:", result);
    const data =
      result?.data?.getLogs ||
      result?.value?.data?.getLogs ||
      [];
    console.log("fetchLogs processed data:", data);
    return data;
  } catch (err) {
    console.log("error fetching logs:", err);
    return [];
  }
}

export async function fetchPolicy(args) {
  const client = generateClient();
  try {
    const result = await client.graphql({
      query: getUserPolicy,
      variables: args
    });
    const data =
      result?.data?.getUserPolicy ||
      result?.value?.data?.getUserPolicy ||
      [];
    return data;
  } catch (err) {
    console.log("error fetching Entitlement", err);
    return [];
  }
}

export async function updateStatus(data) {
  const client = generateClient();
  try {
    const result = await client.graphql({
      query: updateRequests,
      variables: { input: data }
    });
    const updated =
      result?.data?.updateRequests ||
      result?.value?.data?.updateRequests ||
      {};
    return updated;
  } catch (err) {
    console.log("error updating status");
    return {};
  }
}

export async function requestTeam(data) {
  const client = generateClient();
  try {
    const result = await client.graphql({
      query: createRequests,
      variables: { input: data }
    });
    const created =
      result?.data?.createRequests ||
      result?.value?.data?.createRequests ||
      {};
    return created.id;
  } catch (err) {
    console.log("error creating request");
    return null;
  }
}

export async function getSessionLogs(data) {
  const client = generateClient();
  try {
    const result = await client.graphql({
      query: createSessions,
      variables: { input: data }
    });
    const created =
      result?.data?.createSessions ||
      result?.value?.data?.createSessions ||
      {};
    return created.id;
  } catch (err) {
    console.log("error creating session Logs");
    return null;
  }
}

export async function deleteSessionLogs(data) {
  const client = generateClient();
  try {
    const result = await client.graphql({
      query: deleteSessions,
      variables: { input: data }
    });
    const deleted =
      result?.data?.deleteSessions ||
      result?.value?.data?.deleteSessions ||
      {};
    return deleted;
  } catch (err) {
    console.log("error deleting session log");
    return {};
  }
}

export async function getSession(id) {
  const client = generateClient();
  try {
    const result = await client.graphql({
      query: getSessions,
      variables: { id }
    });
    const data =
      result?.data?.getSessions ||
      result?.value?.data?.getSessions ||
      {};
    return data;
  } catch (err) {
    console.log("error fetching session log");
    return {};
  }
}

export async function addApprovers(data) {
  const client = generateClient();
  try {
    const result = await client.graphql({
      query: createApprovers,
      variables: { input: data }
    });
    const created =
      result?.data?.createApprovers ||
      result?.value?.data?.createApprovers ||
      {};
    return created.Id;
  } catch (err) {
    console.log("error adding Approvers");
    return null;
  }
}

export async function delApprover(data) {
  const client = generateClient();
  try {
    const result = await client.graphql({
      query: deleteApprovers,
      variables: { input: data }
    });
    const deleted =
      result?.data?.deleteApprovers ||
      result?.value?.data?.deleteApprovers ||
      {};
    return deleted;
  } catch (err) {
    console.log("error deleting approver");
    return {};
  }
}

export async function editApprover(data) {
  const client = generateClient();
  try {
    const result = await client.graphql({
      query: updateApprovers,
      variables: { input: data }
    });
    const updated =
      result?.data?.updateApprovers ||
      result?.value?.data?.updateApprovers ||
      {};
    return updated;
  } catch (err) {
    console.log("error updating approver");
    return {};
  }
}

export async function fetchApprovers(id, type) {
  const client = generateClient();
  try {
    const result = await client.graphql({
      query: getApprovers,
      variables: { id, type }
    });
    const data =
      result?.data?.getApprovers ||
      result?.value?.data?.getApprovers ||
      null;
    return data;
  } catch (err) {
    console.log("error fetching approvers");
    return null;
  }
}

export async function addPolicy(data) {
  const client = generateClient();
  try {
    const result = await client.graphql({
      query: createEligibility,
      variables: { input: data }
    });
    const created =
      result?.data?.createEligibility ||
      result?.value?.data?.createEligibility ||
      {};
    return created.id;
  } catch (err) {
    console.log("error creating policy");
    return null;
  }
}

export async function delPolicy(data) {
  const client = generateClient();
  try {
    const result = await client.graphql({
      query: deleteEligibility,
      variables: { input: data }
    });
    const deleted =
      result?.data?.deleteEligibility ||
      result?.value?.data?.deleteEligibility ||
      {};
    return deleted;
  } catch (err) {
    console.log("error deleting policy");
    return {};
  }
}

export async function editPolicy(data) {
  const client = generateClient();
  try {
    const result = await client.graphql({
      query: updateEligibility,
      variables: { input: data }
    });
    const updated =
      result?.data?.updateEligibility ||
      result?.value?.data?.updateEligibility ||
      {};
    return updated;
  } catch (err) {
    console.log("error updating policy");
    return {};
  }
}

export async function fetchEligibility(id) {
  const client = generateClient();
  try {
    const result = await client.graphql({
      query: getEligibility,
      variables: { id }
    });
    const data =
      result?.data?.getEligibility ||
      result?.value?.data?.getEligibility ||
      {};
    return data;
  } catch (err) {
    console.log("error fetching eligibility");
    return {};
  }
}

export async function getAllEligibility() {
  const client = generateClient();
  let nextToken = null;
  let data = [];
  try {
    do {
      const result = await client.graphql({
        query: listEligibilities,
        variables: { nextToken }
      });
      const items =
        result?.data?.listEligibilities?.items ||
        result?.value?.data?.listEligibilities?.items ||
        [];
      data = data.concat(items);
      nextToken =
        result?.data?.listEligibilities?.nextToken ||
        result?.value?.data?.listEligibilities?.nextToken;
    } while (nextToken);
    return data;
  } catch (err) {
    console.log("error fetching eligibility");
    return [];
  }
}

export async function getSetting(id) {
  const client = generateClient();
  try {
    const result = await client.graphql({
      query: getSettings,
      variables: { id: id }
    });
    const data =
      result?.data?.getSettings ??
      result?.value?.data?.getSettings ??
      null;
    return data;
  } catch (err) {
    console.log("error fetching settings");
    return null;
  }
}

export async function createSetting(data) {
  const client = generateClient();
  try {
    const result = await client.graphql({
      query: createSettings,
      variables: { input: data }
    });
    const created =
      result?.data?.createSettings ||
      result?.value?.data?.createSettings ||
      {};
    return created.id;
  } catch (err) {
    console.log("error creating settings");
    return null;
  }
}

export async function updateSetting(data) {
  const client = generateClient();
  try {
    const result = await client.graphql({
      query: updateSettings,
      variables: { input: data }
    });
    const updated =
      result?.data?.updateSettings ||
      result?.value?.data?.updateSettings ||
      {};
    return updated;
  } catch (err) {
    console.log("error updating settings");
    return {};
  }
}

export async function revokePim(data) {
  // This function does not use client directly, so no change needed
  try {
    updateRequests(data).then(() => {});
  } catch (err) {
    console.log("error revoking request");
  }
}
